// "Add to home screen" prompt — makes LakayCart feel like an installed app.
//
// Android/Chrome: captures the `beforeinstallprompt` event and surfaces a
// one-tap Install button. iOS Safari has no such event, so we show a short
// "Share → Add to Home Screen" hint instead. Hidden once installed
// (display-mode: standalone) or dismissed (remembered in localStorage).

import { useEffect, useState } from 'react';
import { Download, X, Share } from 'lucide-react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const DISMISS_KEY = 'lakaycart_install_dismissed';

function isStandalone(): boolean {
  return window.matchMedia?.('(display-mode: standalone)').matches
    || (window.navigator as unknown as { standalone?: boolean }).standalone === true;
}
function isIos(): boolean {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !/crios|fxios/i.test(window.navigator.userAgent);
}

export function InstallPrompt() {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [showIosHint, setShowIosHint] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(DISMISS_KEY) === '1'; } catch { return false; }
  });

  useEffect(() => {
    if (dismissed || isStandalone()) return;

    const onPrompt = (e: Event) => { e.preventDefault(); setDeferred(e as BeforeInstallPromptEvent); };
    window.addEventListener('beforeinstallprompt', onPrompt);

    // iOS gives no event — show the manual hint on Safari mobile after a beat.
    let t: ReturnType<typeof setTimeout> | undefined;
    if (isIos()) t = setTimeout(() => setShowIosHint(true), 1500);

    return () => { window.removeEventListener('beforeinstallprompt', onPrompt); if (t) clearTimeout(t); };
  }, [dismissed]);

  const close = () => {
    setDeferred(null); setShowIosHint(false); setDismissed(true);
    try { localStorage.setItem(DISMISS_KEY, '1'); } catch { /* ignore */ }
  };

  const install = async () => {
    if (!deferred) return;
    await deferred.prompt();
    try { await deferred.userChoice; } catch { /* ignore */ }
    close();
  };

  if (dismissed || (!deferred && !showIosHint)) return null;

  return (
    <div
      className="fixed inset-x-3 z-40 sm:left-auto sm:right-4 sm:w-80 bottom-[calc(env(safe-area-inset-bottom)+72px)] lg:bottom-4"
      role="dialog"
      aria-label="Install VoyeLakay"
    >
      <div className="bg-white border border-[var(--color-border)] rounded-2xl shadow-xl p-4 flex items-start gap-3">
        <span className="inline-flex w-10 h-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-brand-500 to-brand-700 text-white">
          <Download className="w-5 h-5" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[13.5px] font-semibold text-[var(--color-ink)]">Install VoyeLakay</p>
          {deferred ? (
            <>
              <p className="mt-0.5 text-[12px] text-[var(--color-secondary-2)] leading-snug">Add it to your home screen for one-tap access — works offline too.</p>
              <button onClick={install} className="mt-2.5 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-brand-600 text-white text-[12.5px] font-semibold hover:bg-brand-700">
                <Download className="w-3.5 h-3.5" /> Install app
              </button>
            </>
          ) : (
            <p className="mt-0.5 text-[12px] text-[var(--color-secondary-2)] leading-snug">
              Tap <Share className="inline w-3.5 h-3.5 -mt-0.5 text-[var(--color-secondary)]" /> <strong className="text-[var(--color-body)]">Share</strong>, then <strong className="text-[var(--color-body)]">Add to Home Screen</strong>.
            </p>
          )}
        </div>
        <button onClick={close} className="p-1 -mt-1 -mr-1 rounded-md hover:bg-[var(--color-panel)] text-[var(--color-muted)] shrink-0" aria-label="Dismiss">
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
